import { BorrowerContractualizationResponse } from 'api/quote';
import { SimulationDocumentDto } from 'api/documents';
import { createStoreWithReducer, shallow } from 'shared/store';
import { documentsAPI } from 'services/swagger-api/documentsAPI';

// ============================================
// Types
// ============================================

export interface BorrowerContractualizationState {
  globalInfos: BorrowerContractualizationResponse | null;
  borrowerId: string;
  title: string;
  isLoading: boolean;
  error: string | null;
  // Documents state
  documents: SimulationDocumentDto[];
  isFetchingDocuments: boolean;
}

type BorrowerContractualizationAction =
  | { type: 'SET_GLOBAL_INFOS'; payload: BorrowerContractualizationResponse }
  | { type: 'SET_BORROWER_ID'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_DOCUMENTS'; payload: SimulationDocumentDto[] }
  | { type: 'SET_IS_FETCHING_DOCUMENTS'; payload: boolean }
  | { type: 'RESET' };

// ============================================
// Initial State & Reducer
// ============================================

const initialState: BorrowerContractualizationState = {
  globalInfos: null,
  borrowerId: '',
  title: '',
  isLoading: false,
  error: null,
  documents: [],
  isFetchingDocuments: false
};

const borrowerContractualizationReducer = (
  state: BorrowerContractualizationState,
  action: BorrowerContractualizationAction
): BorrowerContractualizationState => {
  switch (action.type) {
    case 'SET_BORROWER_ID':
      return { ...state, borrowerId: action.payload };
    case 'SET_GLOBAL_INFOS':
      return { ...state, globalInfos: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'SET_DOCUMENTS':
      return { ...state, documents: action.payload };
    case 'SET_IS_FETCHING_DOCUMENTS':
      return { ...state, isFetchingDocuments: action.payload };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
};

// ============================================
// Store
// ============================================

/**
 * Store pour les données de contractualisation emprunteur.
 *
 * NOTE: Ce store est peuplé par fetchSubscriptionProcess (dans SubscriptionProcessContext).
 * Pour rafraîchir les données, utiliser refreshSubscriptionProcess.
 * Les documents sont récupérés séparément via fetchBorrowerDocuments.
 */
export const useBorrowerContractualizationStore = createStoreWithReducer<
  BorrowerContractualizationState,
  BorrowerContractualizationAction,
  {
    // Sync actions
    setBorrowerId: (id: string) => void;
    setGlobalInfos: (data: BorrowerContractualizationResponse) => void;
    setLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;
    setDocuments: (documents: SimulationDocumentDto[]) => void;
    setIsFetchingDocuments: (fetching: boolean) => void;
    reset: () => void;
    // Async actions
    fetchBorrowerDocuments: (quoteId: string) => Promise<void>;
  }
>(borrowerContractualizationReducer, initialState, (dispatch, getState) => {
  const syncActions = {
    setBorrowerId: (id: string) =>
      dispatch({ type: 'SET_BORROWER_ID', payload: id }),

    setGlobalInfos: (data: BorrowerContractualizationResponse) =>
      dispatch({ type: 'SET_GLOBAL_INFOS', payload: data }),

    setLoading: (loading: boolean) =>
      dispatch({ type: 'SET_LOADING', payload: loading }),

    setError: (error: string | null) =>
      dispatch({ type: 'SET_ERROR', payload: error }),

    setDocuments: (documents: SimulationDocumentDto[]) =>
      dispatch({ type: 'SET_DOCUMENTS', payload: documents }),

    setIsFetchingDocuments: (fetching: boolean) =>
      dispatch({ type: 'SET_IS_FETCHING_DOCUMENTS', payload: fetching }),

    reset: () => dispatch({ type: 'RESET' })
  };

  return {
    ...syncActions,

    fetchBorrowerDocuments: async (quoteId: string) => {
      try {
        syncActions.setIsFetchingDocuments(true);
        syncActions.setError(null);

        const response = await documentsAPI.getSimulationDocumentsByQuoteID({
          quoteId
        });

        syncActions.setDocuments(response.documents || []);
      } catch (error) {
        console.error(
          'Erreur lors de la récupération des documents borrower:',
          error
        );
        syncActions.setError(
          error instanceof Error
            ? error.message
            : 'Failed to fetch borrower documents'
        );
      } finally {
        syncActions.setIsFetchingDocuments(false);
      }
    }
  };
});

// ============================================
// Hooks de sélection
// ============================================

/**
 * Sélectionne les informations globales du borrower
 */
export const useBorrowerGlobalInfos = () =>
  useBorrowerContractualizationStore(state => state.globalInfos);

/**
 * Sélectionne l'ID du borrower
 */
export const useBorrowerId = () =>
  useBorrowerContractualizationStore(state => state.borrowerId);

/**
 * Sélectionne le titre
 */
export const useBorrowerTitle = () =>
  useBorrowerContractualizationStore(state => state.title);

/**
 * Sélectionne l'état de chargement
 */
export const useBorrowerIsLoading = () =>
  useBorrowerContractualizationStore(state => state.isLoading);

/**
 * Sélectionne l'erreur
 */
export const useBorrowerError = () =>
  useBorrowerContractualizationStore(state => state.error);

/**
 * Sélectionne les documents
 */
export const useBorrowerDocumentsFromStore = () =>
  useBorrowerContractualizationStore(state => state.documents, shallow);

/**
 * Sélectionne l'état de récupération des documents
 */
export const useBorrowerIsFetchingDocuments = () =>
  useBorrowerContractualizationStore(state => state.isFetchingDocuments);

/**
 * Sélectionne l'état complet (avec shallow compare)
 */
export const useBorrowerContractualizationState = () =>
  useBorrowerContractualizationStore(
    state => ({
      globalInfos: state.globalInfos,
      borrowerId: state.borrowerId,
      title: state.title,
      isLoading: state.isLoading,
      error: state.error,
      documents: state.documents,
      isFetchingDocuments: state.isFetchingDocuments
    }),
    shallow
  );

/**
 * Retourne toutes les actions du store
 */
export const useBorrowerContractualizationActions = () =>
  useBorrowerContractualizationStore.actions;

/**
 * Accès direct au store (hors composants React)
 */
export const borrowerContractualizationStore = {
  getState: useBorrowerContractualizationStore.getState,
  setState: useBorrowerContractualizationStore.setState,
  dispatch: useBorrowerContractualizationStore.dispatch,
  actions: useBorrowerContractualizationStore.actions,
  subscribe: useBorrowerContractualizationStore.subscribe
};
